import authService from '../utils/auth.js';
import { getApiBaseUrl } from '../utils/apiBase.js';

function authHeaders() {
  const token = authService.getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function downloadResponse(response, fallbackName) {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || `Request failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return {
    blob,
    filename: match?.[1] || fallbackName,
    headers: response.headers
  };
}

export async function mergePdfs(files) {
  const form = new FormData();
  files.forEach((file) => form.append('pdfs', file, file.name));
  const response = await fetch(`${getApiBaseUrl()}/api/tools/pdf/merge`, {
    method: 'POST',
    headers: authHeaders(),
    body: form
  });
  return downloadResponse(response, 'merged.pdf');
}

export async function splitPdf(file, startPage, endPage) {
  const form = new FormData();
  form.append('pdf', file, file.name);
  if (startPage != null) form.append('startPage', String(startPage));
  if (endPage != null) form.append('endPage', String(endPage));
  const response = await fetch(`${getApiBaseUrl()}/api/tools/pdf/split`, {
    method: 'POST',
    headers: authHeaders(),
    body: form
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || `Request failed (${response.status})`);
  }
  const pages = (data.data?.pages || []).map((item) => {
    const binary = atob(item.base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return {
      page: item.page,
      filename: item.filename || `page-${item.page}.pdf`,
      blob: new Blob([bytes], { type: 'application/pdf' })
    };
  });
  if (pages.length === 0) {
    throw new Error('Split returned no pages');
  }
  return {
    pages,
    pageCount: data.data?.pageCount,
    startPage: data.data?.startPage,
    endPage: data.data?.endPage
  };
}

export async function compressPdf(file) {
  const form = new FormData();
  form.append('pdf', file, file.name);
  const response = await fetch(`${getApiBaseUrl()}/api/tools/pdf/compress`, {
    method: 'POST',
    headers: authHeaders(),
    body: form
  });
  return downloadResponse(response, 'compressed.pdf');
}

export async function convertFile(file, targetFormat = 'pdf') {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('targetFormat', targetFormat);
  const response = await fetch(`${getApiBaseUrl()}/api/tools/convert`, {
    method: 'POST',
    headers: authHeaders(),
    body: form
  });
  return downloadResponse(response, `converted.${targetFormat}`);
}

/** @deprecated use convertFile */
export async function convertToPdf(file) {
  return convertFile(file, 'pdf');
}

export async function fetchConvertCapabilities() {
  const response = await fetch(`${getApiBaseUrl()}/api/tools/convert/capabilities`, {
    headers: authHeaders()
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Could not load convert capabilities');
  }
  return data.data || {};
}

export function triggerDownload({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Download several files with a short gap so browsers don't block them. */
export async function triggerDownloads(files, gapMs = 350) {
  for (let i = 0; i < files.length; i += 1) {
    triggerDownload(files[i]);
    if (i < files.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, gapMs));
    }
  }
}
